'use client'

import { Calendar, Popover, PopoverContent, PopoverTrigger } from '@repo/ui'
import { Undo2 } from 'lucide-react'
import { useState } from 'react'

import { formatDay, fromISO, todayISO, toISO } from '@/lib/dates'
import { dayWord, type RentalTermView, returnDay } from '@/lib/rental'
import { RADIUS, SURFACE_SOLID } from '@/lib/surface'
import { META, MICRO } from '@/lib/typography'
import { cn } from '@/lib/utils'

/**
 * Collection day and return day, in one calendar (spec §11 · I4).
 *
 * ── WHY IT IS NOT A RANGE CALENDAR ───────────────────────────────────────
 * Spec §11 sells five lengths — 1, 3, 7, 14 and 30 days — and adds "inne
 * długości niedozwolone". A range calendar accepts any two dates, so it offers
 * a five-day hire the checkout then refuses; the panel's answer to that used to
 * be a row of length pills and a single day picker, which is correct but asks
 * the reader to hold a subtraction in their head ("if I take it on the 12th and
 * pick 3 doby, when am I bringing it back?").
 *
 * So this is a range calendar with the illegal ranges removed. Pick the day you
 * collect, and the only days that stay live are the five the price list
 * actually has — each one labelled underneath with its length and its price.
 * The constraint stops being a rule somebody has to read and becomes the shape
 * of the control.
 *
 * ── TWO PHASES, ONE POPOVER ──────────────────────────────────────────────
 * Phase one is an ordinary day picker. Phase two keeps the same month in view
 * and disables everything except the five return days — staying put matters,
 * because a calendar that jumps to another month between clicks loses the day
 * that was just chosen.
 *
 * The draft is local until BOTH ends exist. A half-made window handed upward
 * would let the rail quote a price for a term nobody had finished choosing.
 */
export function RentalWindowPicker({
  terms,
  from,
  picked,
  onPick,
  className,
  id,
}: {
  /** The five lengths this item is sold in, cheapest first. */
  terms: readonly RentalTermView[]
  /** The collection day already settled on, or `''`. */
  from: string
  /** The length already settled on, or `null`. */
  picked: RentalTermView | null
  /** Both ends at once — see the note about half-made windows above. */
  onPick: (from: string, term: RentalTermView) => void
  className?: string
  id?: string
}) {
  const [open, setOpen] = useState(false)
  /** The collection day being tried out, before a return day confirms it. */
  const [draft, setDraft] = useState('')

  const floor = todayISO()
  const start = fromISO(floor)
  const collecting = draft || (picked ? '' : from)

  /** Every legal return day for the draft, keyed by ISO so lookup is one hit. */
  const returns = new Map<string, RentalTermView>(
    collecting ? terms.map((term) => [returnDay(collecting, term.days), term]) : [],
  )
  const returnDates = [...returns.keys()].map((iso) => fromISO(iso)).filter((d) => d !== null)

  const reset = () => setDraft('')

  const label =
    from && picked ? `${formatDay(from)} → ${formatDay(returnDay(from, picked.days))}` : ''

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        // A popover closed mid-choice must not reopen half-way through one.
        if (!next) reset()
      }}
    >
      <PopoverTrigger
        id={id}
        aria-label={label ? `Termin najmu: ${label}` : 'Kiedy odbierasz?'}
        className={cn(
          'grid w-full grid-cols-2 gap-2 rounded-2xl text-left outline-none focus-visible:ring-2 focus-visible:ring-white/40 focus-visible:ring-offset-2 focus-visible:ring-offset-black',
          className,
        )}
      >
        {/* Two fields, one trigger: „Odbiór" and „Zwrot" read as the two ends
            of the hire (owner's mock-up A), and either opens the same
            calendar — a range is picked in one place, start then end. */}
        <WindowField label="Odbiór" value={from ? formatDay(from) : 'Wybierz dzień'} open={open} />
        <WindowField
          label="Zwrot"
          value={from && picked ? formatDay(returnDay(from, picked.days)) : '—'}
          open={open}
        />
      </PopoverTrigger>

      <PopoverContent
        align="start"
        className={cn('w-auto p-0 text-white', 'z-[100]', RADIUS.box, SURFACE_SOLID)}
        collisionPadding={16}
      >
        <div className="border-white/10 border-b px-4 py-3">
          <p className={cn(META, 'font-semibold text-white')}>
            {collecting ? 'Kiedy zwracasz?' : 'Kiedy odbierasz?'}
          </p>
          <p className={cn(MICRO, 'mt-0.5 text-white/55')}>
            {collecting ? (
              <>Odbiór {formatDay(collecting)}. Podświetlone są dni, na które mamy cennik.</>
            ) : (
              'Najpierw dzień odbioru, potem pokażemy możliwe zwroty.'
            )}
          </p>
        </div>

        <Calendar
          mode="single"
          numberOfMonths={1}
          autoFocus
          selected={fromISO(collecting) ?? undefined}
          onSelect={(next) => {
            if (!next) return
            const iso = toISO(next)
            if (!collecting) {
              setDraft(iso)
              return
            }
            const term = returns.get(iso)
            // Only a legal return day commits. Everything else is disabled, so
            // this is a guard against a keyboard reaching what a pointer cannot.
            if (!term) return
            onPick(collecting, term)
            reset()
            setOpen(false)
          }}
          {...(start ? { startMonth: start } : {})}
          disabled={
            collecting
              ? (day: Date) => !returns.has(toISO(day))
              : start
                ? { before: start }
                : undefined
          }
          modifiers={
            collecting
              ? {
                  zwrot: returnDates as Date[],
                  ...(fromISO(collecting) ? { odbior: [fromISO(collecting) as Date] } : {}),
                }
              : undefined
          }
          modifiersClassNames={{
            // The five live days carry the brand ramp, so "possible" is
            // something the eye finds before the pointer does.
            zwrot: 'font-bold text-white [background:var(--flou-gradient-solid)] rounded-md',
            // The collection day stays clickable — react-day-picker never
            // disables the selected day — so it has to LOOK like the day
            // already chosen rather than like a sixth option that does nothing
            // when pressed. Outlined, not filled: it is a marker, not a target.
            odbior: 'rounded-md font-bold text-white ring-2 ring-white/50 ring-inset',
          }}
          {...(fromISO(collecting) ? { defaultMonth: fromISO(collecting) ?? undefined } : {})}
          className="[--cell-size:2.5rem] sm:[--cell-size:2.35rem]"
          classNames={{
            weekday: 'flex-1 text-[0.75rem] font-medium text-white/55 capitalize select-none',
            caption_label: 'text-[0.9rem] font-semibold capitalize select-none',
            outside: 'text-white/55',
          }}
        />

        {collecting ? (
          <div className="border-white/10 border-t p-3">
            {/* The same five days as a list, because a highlighted square says
                "you may" and not "how long" or "how much". Clicking one is the
                keyboard-free path to the identical outcome. */}
            <ul className="flex flex-col gap-1">
              {terms.map((term) => {
                const day = returnDay(collecting, term.days)
                return (
                  <li key={term.days}>
                    <button
                      type="button"
                      onClick={() => {
                        onPick(collecting, term)
                        reset()
                        setOpen(false)
                      }}
                      className={cn(
                        MICRO,
                        'flex w-full items-center justify-between gap-3 rounded-xl border border-transparent px-3 py-2 text-left transition-colors hover:bg-white/[0.07]',
                      )}
                    >
                      <span className="font-semibold text-white">
                        {term.days} {dayWord(term.days)}
                        <span className="ml-2 font-medium text-white/55">
                          zwrot {formatDay(day)}
                        </span>
                      </span>
                      <span className="shrink-0 font-bold text-white">
                        {Math.round(term.priceNet)} zł
                      </span>
                    </button>
                  </li>
                )
              })}
            </ul>

            <button
              type="button"
              onClick={reset}
              className={cn(
                MICRO,
                'mt-1 inline-flex items-center gap-1.5 px-3 py-2 font-semibold text-white/55 transition-colors hover:text-white',
              )}
            >
              <Undo2 size={12} aria-hidden="true" />
              Zmień dzień odbioru
            </button>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  )
}

function WindowField({ label, value, open }: { label: string; value: string; open: boolean }) {
  return (
    <span
      className={cn(
        'flex min-h-[4.25rem] flex-col justify-center gap-0.5 rounded-2xl border bg-white/[0.04] px-4 py-3 transition-colors',
        open ? 'border-white' : 'border-white/12 hover:border-white/30',
      )}
    >
      <span className={cn(MICRO, 'text-white/55')}>{label}</span>
      <span className="font-masthead text-[1.05rem] font-semibold text-white">{value}</span>
    </span>
  )
}
